import type { Account } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';

import { Loader } from '../layout/loading';
import CopyableCode from '../shared/copyableCode';

export const VaultOwnBucketCopyablePolicy = ({
  bucketName,
  restoreAccounts,
}: {
  bucketName: string;
  restoreAccounts?: Account[];
}) => {
  const dal = useDAL();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.list();

  if (isLoadingScanningAccounts) {
    return <Loader />;
  }

  const scanningAccountId =
    scanningAccounts?.accounts.find(
      (account) => account.cloudProvider === Provider.Aws
    )?.providerAccountId || '';

  return (
    <CopyableCode
      title='policy.json'
      code={getCode(bucketName, scanningAccountId, restoreAccounts)}
      maxHeight='320px'
    />
  );
};

const getCode = (
  bucketName: string,
  scanningAccountId: string,
  restoreAccounts?: Account[]
) => {
  return `
  {
    "Version": "2012-10-17",
    "Statement": [
      {
          "Effect": "Allow",
          "Principal": {
              "AWS": "arn:aws:iam::${scanningAccountId}:role/EonScanningNodeRole"
          },
          "Action": [
              "s3:GetObject",
              "s3:PutObject",
              "s3:DeleteObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      }${restoreAccounts?.length ? getRestoreAccountsCode(restoreAccounts, bucketName) : ''}
    ]
}`;
};

const getRestoreAccountsCode = (
  restoreAccounts: Account[],
  bucketName: string
) => {
  return `,
      {
          "Sid": "AllowRestoreAccess",
          "Effect": "Allow",
          "Principal": {
                  "AWS": "[${restoreAccounts.map((account) => `arn:aws:iam::${account.providerAccountId}:role/EonRestoreNodeRole`).join(',')}]"
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ]
      },
      {
          "Sid": "AthenaGetSchemaJson",
          "Effect": "Allow",
          "Principal": {
              "AWS": "[${restoreAccounts.map((account) => `arn:aws:iam::${account.providerAccountId}:role/${account.role}`).join(',')}]"
          },
          "Action": "s3:GetObject",
          "Resource": [
              "arn:aws:s3:::${bucketName}/db-export/*/eon-*/*/*/_schema.json",
              "arn:aws:s3:::${bucketName}/*/db-export/*/eon-*/*/*/_schema.json"
          ]
      },
      {
          "Sid": "AthenaQueryPolicy",
          "Effect": "Allow",
          "Principal": {
              "AWS": "[${restoreAccounts.map((account) => `arn:aws:iam::${account.providerAccountId}:role/${account.role}`).join(',')}]"
          },
          "Action": [
              "s3:GetObject",
              "s3:ListBucket"
          ],
          "Resource": [
              "arn:aws:s3:::${bucketName}",
              "arn:aws:s3:::${bucketName}/*"
          ],
          "Condition": {
              "ForAllValues:StringEquals": {
                  "aws:CalledVia": "athena.amazonaws.com"
              }
          }
      }`;
};
